import React, { useEffect, useState } from "react";
import { Grid, Box, Button, Typography, MenuItem, Select, FormControl, InputLabel, } from "@mui/material";
import { BarChart } from '@mui/x-charts/BarChart';
import StackedBarChart from '../../components/StackedBarChart/StackedBarChart';
import CustomPieChart from "../DonutChart/DonutChart";
import AgingTable from '../../components/AgingTable/AgingTable';
import StackedBarChartbyDynamicLegends from '../../components/StackedBarChart/StackedBarChartbyDynamicLegends';
import { CircularProgress } from "@mui/material";

export default function SupplierDashboard({ data, onReportFilterApply, onClear, initialFilters, isPending, invoiceStatusListData, invoiceRejectionListData }) {
    const [supplierDatabyVal, setSupplierValueData] = useState([]);
    const [supplierDataByVol, setSupplierDataByVol] = useState([]);
    const [supplierByRejRate, setSupplierByRejRate] = useState([]);
    const [invoiceComplexityData, setInvoiceComplexityData] = useState([]);
    const [supplierAgingData, setSupplierAgingData] = useState([]);
    const [rejectionReasons, setRejectionReasons] = useState([]);
    const [supplierFilters, setSupplierFilters] = useState({
        invoice_status: initialFilters.invoice_status || [],
        complexity_type: initialFilters.complexity_type || [],
        rejection_type: initialFilters.rejection_type || []
    })

    useEffect(() => {
        if (data?.total_by_supplier?.length) {
            setSupplierValueData(data?.total_by_supplier);
        }
        if (data?.top_by_volume?.length) {
            setSupplierDataByVol(data?.top_by_volume);
        }
        if (data?.top_rejection_rate?.length) {
            setSupplierByRejRate(data?.top_rejection_rate);
        }
        if (data?.complexity_pie?.length) {
            const complexityPieTransformed = data?.complexity_pie.map((item, index) => ({
                id: index,
                value: item.count,
                label: item.complexity.charAt(0).toUpperCase() + item.complexity.slice(1).replace(/_/g, ' ').replace(/\bpo\b/g, "PO")
            }))
            setInvoiceComplexityData(complexityPieTransformed);
        }
        if (data?.rejection_reasons?.length) {
            setRejectionReasons(data?.rejection_reasons);
        }
    }, [data])

    useEffect(() => {
        if (data?.exceptions?.length) {
            setSupplierAgingData(data?.exceptions);
        } else {
            setSupplierAgingData([])
        }
    }, [data?.exceptions])

    useEffect(() => {
        setSupplierFilters({
            invoice_status: initialFilters?.invoice_status || [],
            complexity_type: initialFilters?.complexity_type || [],
            rejection_type: initialFilters?.rejection_type || []
        })
    }, [initialFilters])

    const handleChange = (event) => {
        setSupplierFilters((prev) => {
            return {
                ...prev,
                [event.target.name]: event.target.value
            }
        })
    }
    const handleApply = () => {
        onReportFilterApply(supplierFilters)
    }

    const handleReset = () => {
        setSupplierFilters({
            invoice_status: [],
            complexity_type: [],
            rejection_type: []

        })
        onClear && onClear(3);
    }

    // color palette
    const palette = [
        '#3A238C',
        '#543EA3',
        '#6754AD',
        '#7B6AB8',
        '#9384C7',
        '#A296CC',
        '#B9AFDB',
        '#D1CAEB',
        '#E2DCF2',
        '#F0EDFA'
    ];

    //Prepare series and data
    const series = [
        {
            dataKey: "count",
            label: "Invoices",
            color: "#543EA3"
        }
    ];

    const complexityTypes = [
        { value: "Single_Po", label: "Single PO" },
        { value: "Multi_Po", label: "Multi PO" },
        { value: "Non_Po", label: "Non PO" },
        { value: "Yet_To_Be_Extracted", label: "Yet To Be Extracted" },
    ];

    const rejectionTypes = [
        "automatic", "manual"
    ];
    const supplierRejSeries = [
        {
            dataKey: "auto_rejections",
            label: "Automatic rejection",
            color: "rgba(84, 62, 163, 1)",
            stack: 'rejection',
        },
        {
            dataKey: "manual_rejections",
            label: "Manual rejection",
            color: "rgba(84, 62, 163, .4)",
            stack: 'rejection',
        }
    ];

    const formatValue = (value: string) => {
        if (!value) return "";
        return value
            .replace(/_/g, " ")
            .split(" ")
            .map(word => word.toLowerCase() === "po" ? "PO" : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    };

    const columns = [
        {
            field: 'invoiceid',
            headerName: 'Invoice ID',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'supplier',
            headerName: 'Supplier Name',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: ({ value }: { value: string }) => formatValue(value)
        },
        {
            field: 'supplierid',
            headerName: 'Supplier Id',
            flex: 1,
            align: 'left',
            headerAlign: 'left'
        },
        {
            field: 'complexity',
            headerName: 'Complexity',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: ({ value }: { value: string }) => formatValue(value)
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: ({ value }: { value: string }) => formatValue(value)
        },
        {
            field: 'age',
            headerName: 'Age (in days)',
            type: 'number',
            flex: 1,
            align: 'left',
            headerAlign: 'left'
        },
    ] as const;

    const coloursByStatusDonut = ['rgba(84, 62, 163, 1)', 'rgba(84, 62, 163, 0.8)', 'rgba(84, 62, 163, 0.6)', 'rgba(84, 62, 163, 0.4)'
    ];

    return (
        <div style={{ background: 'var(--background-off-white)', borderRadius: '12px', padding: '32px' }}>
            {
                isPending ? <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress sx={{ margin: '0 auto', mt: "2%" }} /></Box> : <>

                    <Box sx={{ width: "100%", borderRadius: "8px", mb: 4 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-end', // vertical alignment
                                gap: 2,
                                width: '100%',
                                background: 'var(--background-off-white)',
                                borderRadius: '8px',
                                mb: 2,
                            }}
                        >
                            {/* Invoice Status */}
                            <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column", width: '20%' }}>
                                <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>
                                    Invoice status
                                </InputLabel>
                                <FormControl fullWidth size="small">
                                    <Select
                                        multiple
                                        value={supplierFilters.invoice_status}
                                        onChange={handleChange}
                                        name="invoice_status"
                                        displayEmpty
                                        renderValue={selected =>
                                            selected.length
                                                ? selected.map(opt => opt.replaceAll("_", " ")).join(",")
                                                : <span style={{ color: 'var(--brand-text-default)', fontSize: '14px' }}>Select invoice status</span>
                                        }
                                        sx={{ background: 'var(--white)', height: '38px', borderRadius: '6' }}
                                    >
                                        {invoiceStatusListData.map(value => (
                                            <MenuItem value={value} key={value}>
                                                {value.replaceAll("_", " ").replace(/\bErp\b/g, "ERP")}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Complexity Type */}
                            <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column", width: '20%' }}>
                                <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>
                                    Complexity type
                                </InputLabel>
                                <FormControl fullWidth size="small">
                                    <Select
                                        multiple
                                        value={supplierFilters.complexity_type}
                                        onChange={handleChange}
                                        name="complexity_type"
                                        displayEmpty
                                        renderValue={selected =>
                                            selected.length
                                                ? selected.map(opt => opt.replaceAll("_", " ")).join(",")
                                                : <span style={{ color: 'var(--brand-text-default)', fontSize: '14px' }}>Select complexity type</span>
                                        }
                                        sx={{ background: 'var(--white)', height: '38px', borderRadius: '6' }}
                                    >
                                        {complexityTypes.map(type => (
                                            <MenuItem value={type.value} key={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Rejection Type */}
                            <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column", width: '20%' }}>
                                <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>
                                    Rejection type (automatic/manual)
                                </InputLabel>
                                <FormControl fullWidth size="small">
                                    <Select
                                        multiple
                                        value={supplierFilters.rejection_type}
                                        onChange={handleChange}
                                        name="rejection_type"
                                        displayEmpty
                                        renderValue={selected =>
                                            selected.length
                                                ? selected.map(opt => opt.charAt(0).toUpperCase() + opt.slice(1)).join(",")
                                                : <span style={{ color: 'var(--brand-text-default)', fontSize: '14px' }}>Select rejection type</span>
                                        }
                                        sx={{ background: 'var(--white)', height: '38px', borderRadius: '6' }}
                                    >
                                        {rejectionTypes.map(value => (
                                            <MenuItem value={value} key={value}>
                                                {value.charAt(0).toUpperCase() + value.slice(1)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Actions */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2, mb: '2px' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    sx={{ minWidth: '102px', fontSize: '14px', padding: '10px 12px', fontWeight: 600, lineHeight: '18px', height: 'auto' }}
                                    onClick={handleApply}
                                >
                                    Apply filters
                                </Button>
                                <Button variant="outlined" color="secondary" onClick={handleReset} sx={{ fontSize: '14px', fontWeight: 600 }}>
                                    Reset
                                </Button>
                            </Box>
                        </Box>


                    </Box>
                    <div style={{ backgroundColor: "#F5F5F5", display: 'flex', gap: '32px', flexDirection: 'column' }}>
                        <StackedBarChart
                            data={supplierDatabyVal}
                            series={series}
                            xAxisKey="supplier"
                            xAxisLabel="Suppliers"
                            yAxisLabel="Invoices"
                            title="Invoices Flagged for Review: Top 10 Suppliers"
                        />
                        <StackedBarChart
                            data={supplierDataByVol}
                            series={series}
                            xAxisKey="supplier"
                            xAxisLabel="Suppliers"
                            yAxisLabel="Invoices"
                            title="Top suppliers by volume (top 10 suppliers)"
                        />

                        <Box sx={{
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "24px",
                            background: "#fff",
                            border: "1px solid #E5E5E5",
                            borderRadius: "8px",
                        }}>
                            <Typography variant="h6" component="h4" sx={{ fontSize: '20px', fontWeight: 700, color: '#111', mb: 2 }}>
                                Top 10 suppliers by rejection rate
                            </Typography>
                            <BarChart
                                series={supplierRejSeries}
                                xAxis={[
                                    {
                                        scaleType: "band",
                                        dataKey: "supplier",
                                        label: "Suppliers",
                                        valueFormatter: (value, context) => {
                                            if (context.location === "tick") {
                                                return value.length > 0 ? " " : value;
                                            }
                                            return value.charAt(0).toUpperCase() + value.slice(1)
                                        }

                                    },
                                ]}
                                yAxis={[
                                    {
                                        label: "Invoices",
                                        min: 0,
                                        max: 100,
                                        valueFormatter: (v) => `${v}%`
                                    },
                                ]}
                                dataset={supplierByRejRate}
                                height={280}
                                sx={{
                                    '& .MuiChartsAxis-root .MuiChartsAxis-line': { stroke: 'var(--grey-CCC)' },
                                    '& .MuiYAxis-root .MuiYAxis-line': { stroke: 'var(--grey-CCC)' },
                                    '& .MuiChartsAxis-root .MuiChartsAxis-tick': { stroke: 'var(--grey-CCC)' }
                                }}
                                slotProps={{
                                    legend: {
                                        sx: {
                                            width: '100%',
                                            marginLeft: "30px",
                                            height: "40px",
                                            marginBottom: '20px',
                                            flexDirection: 'column',
                                            textTransform: "capitalize",
                                            overflowX: 'auto'
                                        },
                                    },
                                }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: '32px' }}>
                            <CustomPieChart data={invoiceComplexityData} title="Suppliers invoice complexity Pie" colours={coloursByStatusDonut} />
                            <Box sx={{ width: "65%", }}>
                                <StackedBarChartbyDynamicLegends
                                    data={rejectionReasons}
                                    title="Suppliers rejection reasons"
                                    yAxisLabel="Invoices"
                                    xAxisDataKey="supplier"
                                    xAxisLabel="Suppliers rejection reasons"
                                    valueFormatterReq={false}
                                />
                            </Box>
                        </Box>

                        <AgingTable rows={supplierAgingData?.length ? supplierAgingData : []} loading={isPending} columns={columns} title={"Suppliers exception list"} />
                    </div>
                </>}
        </div>
    )
}
